import { gql } from '@soundxyz/gql-string';
import type { SortOrder } from '@graphql/reactQuery';
import { CollectorCommentsDocument, useInfiniteQuery } from '@graphql/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  query CollectorComments($releaseId: UUID!, $pagination: CollectorCommentCursorConnectionArgs!) {
    release(id: $releaseId) {
      id
      collectorComments(pagination: $pagination) {
        edges {
          node {
            id
            postComment {
              id
              author {
                id
                ...CommentAuthor
              }
              content
              lexicalContent
              createdAt
              upvoteCount
              repliesCount
              currentHolderOfRelease
              post {
                __typename
                id
              }
            }
            normalizedSongSlot
            nft {
              id
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

export const useReleaseComments = ({
  releaseId,
  pageSize,
  sortDirection,
}: {
  releaseId: string;
  pageSize: number;
  sortDirection: SortOrder;
}) => {
  const {
    orderedList: data,
    isLoading: loading,
    loadMoreNextPage: loadMore,
    refetch,
    error,
    hasNextPage = false,
  } = useInfiniteQuery(CollectorCommentsDocument, {
    getNextPageParam({ data: { release } }) {
      const pageInfo = release?.collectorComments.pageInfo;
      return pageInfo?.hasNextPage && { after: pageInfo.endCursor };
    },
    filterQueryKey: {
      releaseId,
      pageSize,
      sortDirection,
    },
    variables:
      !!releaseId &&
      (({ pageParam }) => {
        const after = pageParam?.after || null;

        return {
          releaseId,
          pagination: {
            after,
            first: after == null ? pageSize : 10,
            sort: {
              createdAt: sortDirection,
            },
          },
        };
      }),
    list({ release }) {
      return release?.collectorComments.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  return {
    data,
    loadMore,
    loading,
    refetch,
    error,
    hasNextPage,
  };
};
